import { useEffect, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Creditcard, Barcode, Payment, Pix } from '@maistodos/core-icons'
import { useTheme } from '@maistodos/design-system-web'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs'
import { PaymentOption } from '@/modules/checkout/_core/domain/entities'
import { FormBankSlip } from '@/modules/checkout/components/FormBankSlip'
import { FormCard } from '@/modules/checkout/components/FormCard'
import { FormPix } from '@/modules/checkout/components/FormPix'
import { FormRedirect } from '@/modules/checkout/components/FormRedirect'
import { usePaymentTypes } from '@/modules/checkout/hooks/use-payment-types'
import { useTabs } from '@/shared/hooks/use-tabs'
import { PaymentFlowAnalytics } from '@/shared/utils/analytics'

import { getTabKey, getVisibleTabKeys, TABS } from './constants'
import { PaymentOptionsProps } from './types'

export const PaymentOptions = ({
  id,
  amount,
  types,
  installments,
  labels,
  showSetDefaultCard,
  isDefaultChecked,
  isAutomaticPixEnabled,
}: PaymentOptionsProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const availableTypes = useMemo(
    () =>
      isAutomaticPixEnabled
        ? types
        : types.filter(
            (method) => method.type !== PaymentOption.AutomaticPixCopyPaste,
          ),
    [isAutomaticPixEnabled, types],
  )

  const { creditCard, debitCard, bankSlip, pix, automaticPix, redirects } =
    usePaymentTypes(availableTypes)

  const tabs = useMemo(
    () => [...TABS, ...redirects.map(getTabKey)],
    [redirects],
  )

  const { activeTab, getTabPanelProps, getTabProps, setActiveTab } = useTabs({
    tabs,
    defaultTab: null,
  })

  const hasAnyCard = Boolean(creditCard) || Boolean(debitCard)
  const cardLabel = creditCard?.label || debitCard?.label || t('Cartão')
  const showCard = hasAnyCard && activeTab === PaymentOption.CreditCard
  const showBankslip = Boolean(bankSlip && activeTab === PaymentOption.BankSlip)
  const hasAnyPix = Boolean(pix) || Boolean(automaticPix)
  const pixLabel = pix?.label || automaticPix?.label || t('Pix')
  const showPix = hasAnyPix && activeTab === PaymentOption.Pix

  const visibleTabs = useMemo(
    () => getVisibleTabKeys(availableTypes),
    [availableTypes],
  )

  useEffect(() => {
    if (visibleTabs.length === 1) {
      setActiveTab(visibleTabs[0])
    }
  }, [setActiveTab, visibleTabs])

  useEffect(() => {
    if (activeTab) {
      PaymentFlowAnalytics.onClickPaymentType(String(activeTab))
    }
  }, [activeTab])

  return (
    <Tabs>
      <TabsList>
        {hasAnyCard && (
          <TabsTrigger {...getTabProps(PaymentOption.CreditCard)}>
            <Creditcard color={theme.colors.brand500} fontSize="30px" />
            <span className="w-full truncate text-center" title={cardLabel}>
              {cardLabel}
            </span>
          </TabsTrigger>
        )}

        {bankSlip && (
          <TabsTrigger {...getTabProps(PaymentOption.BankSlip)}>
            <Barcode color={theme.colors.brand500} fontSize="30px" />
            <span
              className="w-full truncate text-center"
              title={bankSlip.label || t('Boleto')}
            >
              {bankSlip.label || t('Boleto')}
            </span>
          </TabsTrigger>
        )}

        {hasAnyPix && (
          <TabsTrigger {...getTabProps(PaymentOption.Pix)}>
            <Pix color={theme.colors.brand500} fontSize="30px" />
            <span className="w-full truncate text-center" title={pixLabel}>
              {pixLabel}
            </span>
          </TabsTrigger>
        )}

        {redirects.map((redirect) => (
          <TabsTrigger key={redirect.id} {...getTabProps(getTabKey(redirect))}>
            <Payment color={theme.colors.brand500} fontSize="30px" />
            <span
              className="w-full truncate text-center"
              title={redirect.label || t('Pagamento')}
            >
              {redirect.label || t('Pagamento')}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      {showCard && (
        <TabsContent {...getTabPanelProps(PaymentOption.CreditCard)}>
          <FormCard
            id={id}
            defaultCard={labels.defaultCard}
            showSetDefaultCard={showSetDefaultCard}
            isDefaultChecked={isDefaultChecked}
            creditCard={creditCard}
            debitCard={debitCard}
            installments={installments}
          />
        </TabsContent>
      )}

      {showBankslip && (
        <TabsContent {...getTabPanelProps(PaymentOption.BankSlip)}>
          <FormBankSlip amount={amount} id={id} paymentType={bankSlip!} />
        </TabsContent>
      )}

      {showPix && (
        <TabsContent {...getTabPanelProps(PaymentOption.Pix)}>
          <FormPix
            amount={amount}
            id={id}
            pix={pix}
            automaticPix={automaticPix}
          />
        </TabsContent>
      )}

      {redirects.map((redirect) =>
        activeTab === getTabKey(redirect) ? (
          <TabsContent
            key={redirect.id}
            {...getTabPanelProps(getTabKey(redirect))}
          >
            <FormRedirect id={id} paymentType={redirect} />
          </TabsContent>
        ) : null,
      )}
    </Tabs>
  )
}
